import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  dedicatedNumberService,
} from '../services/api'
import type {
  CountryOption,
} from '../client'
import {
  downloadBlob,
} from '../utils/vcard'

// Country names that take a definite article, so "a number in United States"
// reads as "a number in the United States". pytz gives us bare country names
// with no grammatical metadata, and only the US is reachable today - but the
// next countries queued (the Netherlands, the UK) both need it too.
const NEEDS_THE = new Set(['United States', 'United Kingdom', 'Netherlands', 'Philippines'])

function inCountry(label: string): string {
  return NEEDS_THE.has(label) ? `in the ${label}` : `in ${label}`
}

// Dedicated assistant phone number ($15/mo add-on): a private line for the
// assistant instead of the shared pool. A US line also carries the owner's
// texts (GET-16926). The backend status endpoint is the
// gate — the card renders nothing unless the flag is on for this user (or a
// number is already provisioned, so a flag rollback never hides an active,
// billed number).
//
// The countries offered are pre-flighted server-side against live Twilio
// inventory, so every choice shown here can actually be bought. The first one
// is the backend's best guess at where this user is.
export function DedicatedNumberSection() {
  const [loaded, setLoaded] = useState(false)
  const [available, setAvailable] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
  const [price, setPrice] = useState<string | null>(null)
  const [choices, setChoices] = useState<CountryOption[]>([])
  const [smsCapable, setSmsCapable] = useState<boolean | null>(null)
  const [textingEnabled, setTextingEnabled] = useState(false)
  const [preferUs, setPreferUs] = useState(false)
  const [areaCode, setAreaCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [showReleaseModal, setShowReleaseModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const status = await dedicatedNumberService.getStatus()
      setAvailable(status.available)
      setPhoneNumber(status.phone_number ?? null)
      setPrice(status.monthly_price_formatted ?? null)
      setSmsCapable(status.sms_capable ?? null)
      setTextingEnabled(status.texting_enabled ?? false)
      setChoices(status.country_choices ?? [])
    } catch {
      // Leave the card hidden rather than surface a load error for an add-on.
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Locale decides: the backend ranks the user's own country first. The only
  // choice offered is "give me a US number instead", and only to someone who
  // wasn't already getting one — a US user picking between four +1 countries
  // and Israel is noise on a paid upsell.
  const local = choices[0] ?? null
  const us = choices.find((c) => c.iso_country === 'US') ?? null
  const canPreferUs = local !== null && local.iso_country !== 'US' && us !== null
  const selected = canPreferUs && preferUs ? us : local
  // A US purchase can ask for an area code; left blank, the backend tries the
  // owner's own and falls back to any US number. Not offered elsewhere: the
  // backend ignores it outside the US. No choices at all (the country
  // pre-flight failed) still buys in the US, so the field stays.
  const isUs = (selected?.iso_country ?? 'US') === 'US'
  const requestedAreaCode = isUs && areaCode ? areaCode : undefined
  const areaCodeInvalid = requestedAreaCode !== undefined && !/^\d{3}$/.test(requestedAreaCode)

  const handleDownloadCard = async () => {
    setError(null)
    try {
      const { blob, filename } = await dedicatedNumberService.downloadContactCard()
      downloadBlob(blob, filename)
    } catch {
      setError("Couldn't get your contact card - try again in a minute.")
    }
  }

  const handleProvision = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await dedicatedNumberService.provision(
        selected?.iso_country,
        requestedAreaCode,
      )
      setPhoneNumber(result.phone_number)
      // Straight off the purchase response rather than the pre-purchase
      // state: the buy can land in a different country than was asked for,
      // and re-reading status would leave a window where this is unknown and
      // the card describes the wrong kind of line.
      setSmsCapable(result.sms_capable)
      setTextingEnabled(result.texting_enabled)
      setConfirming(false)
    } catch (e: any) {
      if (e?.response?.status === 402) {
        setError("We couldn't set up billing for the number - check your payment method and try again.")
      } else if (e?.response?.status === 409) {
        setError(requestedAreaCode
          ? `No numbers left in area code ${requestedAreaCode} - try a nearby one, or leave it blank for any US number.`
          : 'No numbers available right now - try again in a minute.')
      } else {
        setError("Something went wrong getting your number - nothing was charged. Try again in a minute.")
      }
    } finally {
      setBusy(false)
    }
  }

  const handleRelease = async () => {
    setBusy(true)
    setError(null)
    try {
      await dedicatedNumberService.release()
      setPhoneNumber(null)
      setShowReleaseModal(false)
      // The status that loaded the card skipped the country choices because a
      // number existed. Without them the next purchase carries no country and
      // silently lands in the US.
      await refresh()
    } catch {
      setShowReleaseModal(false)
      setError("Couldn't release the number - try again or contact support.")
    } finally {
      setBusy(false)
    }
  }

  if (!loaded || (!available && !phoneNumber)) {
    return null
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">Dedicated Phone Number</h3>
          {!phoneNumber && (
            <span className="inline-flex items-center rounded-full bg-gray-900 px-2 py-0.5 text-xs font-semibold tracking-wide text-white">
              NEW
            </span>
          )}
        </div>
        {phoneNumber && (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
            Active
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {phoneNumber ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <span className="text-sm font-medium text-gray-700">{phoneNumber}</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => void handleDownloadCard()}
                className="text-sm font-medium text-gray-500 hover:text-gray-900"
              >
                Download contact card
              </button>
              <button
                onClick={() => setShowReleaseModal(true)}
                disabled={busy}
                className="text-sm font-medium text-gray-500 hover:text-red-600 disabled:opacity-50"
              >
                Release
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Incoming and outgoing calls &mdash; anyone you share it with
            reaches your assistant on it.
            {/* Neither sentence while unknown: guessing here tells an Israeli
                line's owner the opposite of what happens to a text. */}
            {textingEnabled
              ? ' You can text your assistant on it too, from the phone you connected for texting. Texts from anyone else reach your assistant, who passes them on to you.'
              : smsCapable === null
                ? ''
                : smsCapable
                  ? ' Texts to it reach your assistant, who passes them on to you.'
                  : ' Texts to it are not delivered.'}
          </p>
        </>
      ) : confirming ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          {/* "a number in Israel", not "an Israel number" - the label is the
              country name, and there is no adjective form to interpolate. */}
          <p className="text-sm text-gray-700 mb-3">
            Your assistant&rsquo;s own number{selected ? ` ${inCountry(selected.label)}` : ''},
            for incoming and outgoing calls &mdash; give it to anyone who needs
            to reach your assistant.
            {selected?.sms_capable === false
              ? ' Texts to it are not delivered.'
              : selected?.texting_available
                ? ' You can text your assistant on it too, from the phone you connect for texting.'
                : ''}
            {price ? ` ${price}/mo, cancel anytime.` : ''}
          </p>
          {canPreferUs && (
            <button
              onClick={() => setPreferUs(!preferUs)}
              disabled={busy}
              className="block mb-3 text-sm text-gray-500 underline hover:text-gray-900 disabled:opacity-50"
            >
              {preferUs
                ? `Use a number ${local ? inCountry(local.label) : 'in my country'} instead`
                : 'Use a US number instead'}
            </button>
          )}
          {isUs && (
            <label className="block mb-3 text-sm text-gray-700">
              Area code <span className="text-gray-500">(optional)</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={3}
                value={areaCode}
                onChange={(e) => {
                  setAreaCode(e.target.value.replace(/\D/g, ''))
                  setError(null)
                }}
                disabled={busy}
                placeholder="e.g. 646"
                aria-label="Area code"
                className="ml-2 w-20 px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
              />
              {areaCodeInvalid && (
                <span className="ml-2 text-xs text-red-600">Enter three digits</span>
              )}
            </label>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={() => void handleProvision()}
              disabled={busy || areaCodeInvalid}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {busy ? 'Setting up…' : `Get my number${price ? ` - ${price}/mo` : ''}`}
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              Not now
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
        >
          Give your assistant its own calling number
          {price ? ` - ${price}/mo` : ''}
        </button>
      )}

      {showReleaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Release your dedicated number?
            </h3>
            <p className="text-sm text-gray-500 mb-2">
              Callers who saved {phoneNumber} will no longer reach your
              assistant, and the number can't be recovered later.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              The monthly charge stops immediately with a prorated credit.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowReleaseModal(false)}
                disabled={busy}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Keep my number
              </button>
              <button
                onClick={() => void handleRelease()}
                disabled={busy}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {busy ? 'Releasing…' : 'Release number'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
